<template>
  <div>
    <div class="title is-3">Gearing with Assassin's gear</div>
    <div class="subtitle is-5">Find all possible setups using Assassin's gear</div>
    <div class="columns">
      <div class="column is-one-quarter">
        <div class="title is-5">Options</div>
        <b-field label="Needed Precision">
          <b-numberinput min="0" :max="max_reachable_precision" v-model="target_precision" controls-position="compact"/>
        </b-field>
        <b-field label="Precision infusions (+5)">
          <b-slider v-model="infusions" :min="0" :max="18" :step="1" ticks lazy/>
        </b-field>
        <b-field label="Weapon type">
          <div>
            <b-radio v-model="two_handed"
                     name="two_handed"
                     :native-value="false">
              One-handed
            </b-radio>
            <b-radio v-model="two_handed"
                     name="two_handed"
                     :native-value="true">
              Two-handed
            </b-radio>
          </div>
        </b-field>
        <b-field label="Gear rarity">
          <b-radio :value="true" :native-value="true">
            Ascended
          </b-radio>
        </b-field>
      </div>
      <div class="column">
        <div class="title is-5">Explanation</div>
        <div class="content">
          <p>
            This is a calculator that finds all possible setups that provide a specified amount of Precision, using a
            full Berserker's setup as a base.
          </p>
          <p>
            This is useful in case a power build does not reach the critical rate cap; there are often multiple setups
            with the same stat amounts. Some may be easier to acquire for you than others and this calculator lists all
            of them.
          </p>
          <p>
            The calculator does not account for potential interactions with stat conversion traits or foods. It only
            uses 1 to 1 exchanges of Power and Precision (swapping Berserker's gear pieces to Assassin's). Keep in mind
            that it may be worth using Accuracy sigils, use a full-blown gear optimizer for those.
          </p>
          <div class="title is-5">Example of usage</div>
          <p>
            Let's say we want to build a Power Chronomancer in March 2021. The target precision for crit cap is
            2995 (21 precision for 1% critical chance; Fury corresponds to 420 precision).
          </p>
          <p>
            If we gear using only Berserker's gear, we arrive at 2896 precision with Fury, Spotter and Banners. We are
            missing 99 precision, so we input that into the <b>Needed Precision</b> field. The table below shows us all
            possible Assassin's combinations that convert <em>exactly</em> 99 Power into Precision.
          </p>
          <p>
            Another use is to visit a reputable website with builds and look at their Assassin's gear choices. On each
            Assassin's piece, calculate <em>Precision - Power</em> and sum these numbers. That is our <b>Needed
            Precision</b> again, input the number and the table will tell you all possible ways to reach that value.
          </p>
        </div>
      </div>

    </div>
    <div class="title is-5">Gear options to convert {{ this.target_precision }} Power into Precision</div>
    <b-table :data="options" narrowed hoverable scrollable>
      <template slot-scope="props">
        <b-table-column field="helmet" label="helmet" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/helmet.png')" name="Helmet" />
          </template>
          <stat-tag :assassins="props.row.helmet"/>
        </b-table-column>
        <b-table-column field="shoulders" label="shoulders" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/shoulders.png')" name="Shoulders" />
          </template>
          <stat-tag :assassins="props.row.shoulders"/>
        </b-table-column>
        <b-table-column field="chestplate" label="chestplate" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/chestplate.png')" name="Chestplate"/>
          </template>
          <stat-tag :assassins="props.row.chestplate"/>
        </b-table-column>
        <b-table-column field="gloves" label="gloves" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/gloves.png')" name="Gloves"/>
          </template>
          <stat-tag :assassins="props.row.gloves"/>
        </b-table-column>
        <b-table-column field="pants" label="pants" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/pants.png')" name="Pants"/>
          </template>
          <stat-tag :assassins="props.row.pants"/>
        </b-table-column>
        <b-table-column field="boots" label="boots" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/boots.png')" name="Boots"/>
          </template>
          <stat-tag :assassins="props.row.boots"/>
        </b-table-column>
        <b-table-column field="back" label="back" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/back.png')" name="Back item"/>
          </template>
          <stat-tag :assassins="props.row.back"/>
        </b-table-column>
        <b-table-column field="accessory1" label="accessory1" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/accessory.png')" name="Accessory 1"/>
          </template>
          <stat-tag :assassins="props.row.accessory1"/>
        </b-table-column>
        <b-table-column field="accessory2" label="accessory2" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/accessory.png')" name="Accessory 2"/>
          </template>
          <stat-tag :assassins="props.row.accessory2"/>
        </b-table-column>
        <b-table-column field="amulet" label="amulet" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/amulet.png')" name="Amulet"/>
          </template>
          <stat-tag :assassins="props.row.amulet"/>
        </b-table-column>
        <b-table-column field="ring1" label="ring1" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/ring.png')" name="Ring 1"/>
          </template>
          <stat-tag :assassins="props.row.ring1"/>
        </b-table-column>
        <b-table-column field="ring2" label="ring2" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/ring.png')" name="Ring 2"/>
          </template>
          <stat-tag :assassins="props.row.ring2"/>
        </b-table-column>
        <b-table-column :visible="!two_handed" field="weapon1" label="weapon1" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/weapon-1hand.png')" name="Weapon 1"/>
          </template>
          <stat-tag :assassins="props.row.weapon1"/>
        </b-table-column>
        <b-table-column :visible="!two_handed" field="weapon2" label="weapon2" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/weapon-1hand.png')" name="Weapon 2"/>
          </template>
          <stat-tag :assassins="props.row.weapon2"/>
        </b-table-column>
        <b-table-column :visible="two_handed" field="weapon2h" label="weapon2h" centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/weapon-2hand.png')" name="Two-Handed Weapon"/>
          </template>
          <stat-tag :assassins="props.row.weapon2h"/>
        </b-table-column>
        <b-table-column field="infusions" label="Precision Infusions" numeric centered>
          <template slot="header">
            <OptionHeader :image-src="require('@/assets/calculators/gear-precision/precise-infusion.png')" name="Precise Infusions"/>
          </template>
          {{ props.row.infusions }}
        </b-table-column>
      </template>
      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon
                  icon="emoticon-sad"
                  size="is-large">
              </b-icon>
            </p>
            <p>No options found.</p>
          </div>
        </section>
      </template>
    </b-table>
  </div>
</template>

<script>
import _ from 'lodash';
import StatTag from "@/components/calculators/gear-precision/StatTag";
import OptionHeader from "@/components/calculators/gear-precision/OptionHeader";

// - stats defines how much precision may be gained by picking this.
// - prefer_prev is a deduplication mechanism, stats marked
// as such won't be picked if the previous stat is not picked.

const stats_base = [
  {name: 'helmet', stats: 18},
  {name: 'shoulders', stats: 13},
  {name: 'chestplate', stats: 40},
  {name: 'gloves', stats: 13},
  {name: 'pants', stats: 27},
  {name: 'boots', stats: 13},
  {name: 'back', stats: 23},
  {name: 'accessory1', stats: 36},
  {name: 'accessory2', stats: 36, prefer_prev: true},
  {name: 'amulet', stats: 49},
  {name: 'ring1', stats: 41},
  {name: 'ring2', stats: 41, prefer_prev: true},
];
const stats_weapon_onehanded = [
  {name: 'weapon1', stats: 35},
  {name: 'weapon2', stats: 35, prefer_prev: true},
]
const stats_weapon_twohanded = [
  {name: 'weapon2h', stats: 72},
]

const infusion_precision = 5;

const max_infusion_count = 18;


export default {
  name: 'calculators-killproof',
  components: {
    StatTag,
    OptionHeader,
  },
  data() {
    return {
      infusions: [0,0],
      target_precision: 99,
      two_handed: false,
      max_infusion_count: max_infusion_count,
    }
  },
  computed: {
    min_infusions() {
      return this.infusions[0];
    },
    max_infusions() {
      return this.infusions[1];
    },
    stats() {
      let stats = stats_base;
      if (this.two_handed) {
        stats = stats.concat(stats_weapon_twohanded)
      } else {
        stats = stats.concat(stats_weapon_onehanded)
      }
      return stats;
    },
    max_reachable_precision() {
      return _.sumBy(this.stats, 'stats') + this.max_infusions * infusion_precision;
    },
    options() {
      let setups = [];

      // We make sure that we do not try to calculate with more infusions that possible in case the user inputs
      // a huge number manually.
      let min_infusions = Math.max(this.min_infusions, 0);
      let max_infusions = Math.min(this.max_infusions, max_infusion_count);

      for (let infusions = min_infusions; infusions <= max_infusions; infusions++) {
        setup_loop: for (let setup_number = 0; setup_number < (1 << this.stats.length); setup_number++) {
          let precision_sum = infusions * infusion_precision;
          for (let i = 0; i < this.stats.length; i++) {
            let chosen = (setup_number >>> i) & 1;
            if (chosen) {
              precision_sum += this.stats[i].stats;

              // A filter for deduplication of results, see description for prefer_prev above.
              if (this.stats[i].prefer_prev) {
                let prev_chosen = (setup_number >>> (i - 1)) & 1;
                if (!prev_chosen && chosen) {
                  continue setup_loop;
                }
              }

            }
          }

          if (precision_sum === this.target_precision) {
            let setup = {
              setup_number: setup_number,
              infusions: infusions
            }
            for (let i = 0; i < this.stats.length; i++) {
              setup[this.stats[i].name] = (setup_number >>> i) & 1;
            }

            setups.push(setup);
          }
        }
      }

      return setups;
    }
  }
}
</script>

<style>
</style>
